import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  TrendingUp, Users, Sparkles, CreditCard, DollarSign, AlertTriangle,
  ShieldAlert, Lock, UserPlus, Activity, RefreshCcw, Loader2,
} from "lucide-react";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v ?? 0);
const fmtPct = (v) => `${(v ?? 0).toFixed(1)} %`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};
const relativeDate = (iso) => {
  if (!iso) return "";
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 60) return "à l'instant";
  if (delta < 3600) return `il y a ${Math.floor(delta / 60)} min`;
  if (delta < 86400) return `il y a ${Math.floor(delta / 3600)} h`;
  if (delta < 7 * 86400) return `il y a ${Math.floor(delta / 86400)} j`;
  return fmtDate(iso);
};

export default function PlatformAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/platform/stats");
      setStats(data);
    } catch (e) {
      // 403 si pas admin plateforme
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.is_platform_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading || (user?.is_platform_admin && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-secondary-text flex items-center gap-3"><div className="spinner" /><span>Chargement…</span></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_platform_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app p-6">
        <div className="card p-8 max-w-md text-center">
          <ShieldAlert size={36} className="mx-auto mb-4" style={{ color: "var(--board-red)" }} />
          <h1 className="font-display text-2xl mb-2" style={{ fontWeight: 700 }}>Accès restreint</h1>
          <p className="text-secondary-text text-sm mb-5">
            Tableau de bord réservé à l'éditeur de PitBoard (MJSA INVEST).
          </p>
          <Link to="/" className="btn btn-outline">← Retour à mon espace</Link>
        </div>
      </div>
    );
  }
  if (!stats) return null;

  const { counts, revenue, churn, recent } = stats;

  return (
    <div data-testid="platform-admin-page" className="max-w-7xl mx-auto">
      <header className="mb-8 animate-in flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold mb-3" style={{ color: "var(--pit-yellow)" }}>
            <Lock size={13} strokeWidth={2} /> DASHBOARD PLATEFORME — RÉSERVÉ MJSA INVEST
          </div>
          <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
            Pilotage <span style={{ color: "#FFC400" }}>PitBoard SaaS</span>
          </h1>
          <p className="text-secondary-text mt-3 max-w-3xl text-[15px]">
            Vue business de l'éditeur : abonnés actifs, MRR, churn et activité récente. Mise à jour : {relativeDate(stats.as_of)}.
          </p>
        </div>
        <button onClick={load} disabled={refreshing} className="btn btn-outline" data-testid="platform-refresh">
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} Actualiser
        </button>
      </header>

      {/* KPIs principaux */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <BigKpi
          icon={DollarSign}
          label="MRR (Monthly Recurring)"
          value={fmtEUR(revenue.mrr_ttc_eur)}
          sub={`${fmtEUR(revenue.mrr_ht_eur)} HT · ARR ${fmtEUR(revenue.arr_ttc_eur)}`}
          tone="accent"
          testid="kpi-mrr"
        />
        <BigKpi
          icon={Users}
          label="Abonnés actifs"
          value={counts.active_subscribers}
          sub={`+ ${counts.trialing} en essai · ${counts.total_companies} entreprises totales`}
          tone="accent"
          testid="kpi-active"
        />
        <BigKpi
          icon={UserPlus}
          label="Nouveaux 7 derniers jours"
          value={counts.new_signups_7d}
          sub={`${counts.new_signups_30d} sur 30 jours`}
          testid="kpi-newsignups"
        />
        <BigKpi
          icon={AlertTriangle}
          label="Churn (30 jours)"
          value={fmtPct(churn.rate_pct)}
          sub={`${churn.expired_last_30d} expirés / ${churn.denominator} potentiels`}
          tone={churn.rate_pct > 10 ? "red" : "muted"}
          testid="kpi-churn"
        />
      </div>

      {/* Bloc revenus */}
      <div className="card p-6 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--grey)" }}>Revenus encaissés</div>
        <div className="grid sm:grid-cols-4 gap-5">
          <Mini label="Revenus 30j (TTC)" value={fmtEUR(revenue.revenue_30d_ttc_eur)} accent />
          <Mini label="Revenus total (TTC)" value={fmtEUR(revenue.revenue_total_ttc_eur)} />
          <Mini label="Paiements 30j" value={revenue.nb_payments_30d} />
          <Mini label="Paiements total" value={revenue.nb_payments_total} />
        </div>
      </div>

      {/* Activité récente */}
      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--grey)" }}>
            <UserPlus size={12} /> Dernières inscriptions
          </div>
          <div className="divide-y divide-[var(--border)]">
            {recent.new_signups.length === 0 && (
              <div className="px-5 py-6 text-center text-sm" style={{ color: "var(--grey)" }}>Aucune inscription récente</div>
            )}
            {recent.new_signups.map((c) => (
              <div key={c.company_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`signup-row-${c.company_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate text-sm">{c.name || "—"}</div>
                  <div className="text-xs truncate" style={{ color: "var(--ink-soft)" }}>{c.email || ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <StatusBadge status={c.subscription_status} />
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--grey)" }}>{relativeDate(c.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--grey)" }}>
            <CreditCard size={12} /> Derniers paiements
          </div>
          <div className="divide-y divide-[var(--border)]">
            {recent.payments.length === 0 && (
              <div className="px-5 py-6 text-center text-sm" style={{ color: "var(--grey)" }}>
                Aucun paiement encore. Lorsque vos premiers clients s'abonneront, ils apparaîtront ici en temps réel.
              </div>
            )}
            {recent.payments.map((p, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-xs truncate" style={{ color: "var(--ink-soft)" }}>{p.company_id}</div>
                  <div className="text-[11px]" style={{ color: "var(--grey)" }}>{relativeDate(p.credited_at)}</div>
                </div>
                <div className="font-semibold numerals" style={{ color: "var(--pit-yellow)" }}>
                  {fmtEUR((p.amount_total_cents || 0) / 100)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer info */}
      <div className="rounded-lg p-4 text-xs flex items-start gap-2" style={{ background: "var(--carbon)", color: "var(--ink-soft)" }}>
        <Activity size={14} className="shrink-0 mt-0.5" style={{ color: "var(--pit-yellow)" }} />
        <div>
          Les MRR/ARR sont calculés sur les abonnés actuellement actifs × {fmtEUR(stats.plan_price_ttc)} TTC.
          Le churn correspond aux comptes ayant payé puis non renouvelés dans les 30 derniers jours, sur le total des comptes payants antérieurs.
        </div>
      </div>
    </div>
  );
}

function BigKpi({ icon: Icon, label, value, sub, tone = "muted", testid }) {
  const color = tone === "accent" ? "#FFC400" : tone === "red" ? "#E10600" : "var(--chalk)";
  return (
    <div className="kpi" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="kpi-label">{label}</div>
        <Icon size={16} className="text-tertiary-text shrink-0" strokeWidth={1.6} />
      </div>
      <div className="kpi-value" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] mt-1" style={{ color: "var(--grey)" }}>{sub}</div>}
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] mb-1" style={{ color: "var(--grey)" }}>{label}</div>
      <div className="font-display text-2xl numerals" style={{ fontWeight: 700, color: accent ? "#FFC400" : "var(--chalk)" }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = {
    active:   { label: "Abonné",  color: "#22c55e" },
    trialing: { label: "Essai",   color: "#FFC400" },
    expired:  { label: "Expiré",  color: "#E10600" },
  }[status] || { label: status || "—", color: "var(--grey)" };
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: `${meta.color}1A`, color: meta.color }}>
      {meta.label}
    </span>
  );
}
